import * as Dto from '_models/dtos';
import { SessionData } from '_models/session-data';
import { Dictionary } from '_services/dictionary';
import { UrlService } from '_services/url-service';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, observable } from 'aurelia-framework';
import { HttpClient } from 'aurelia-http-client';
import { Router } from 'aurelia-router';
import * as Rx from 'rxjs/Rx';

@autoinject
export class ColorPicker {
    private _http: HttpClient;
    private _url: UrlService;
    private _subscriptions: Subscription[] = [];
    private _filterChangedEvent = new Rx.Subject();
    private _lastSelectedProductNumber: string = null;

    // Filter Changed States
    private _isColorCollectionFilterChanged = false;
    private _isColorFamiliesFilterChanged = false;
    private _isLrvFilterChanged = false;

    constructor(
        private _eventBus: EventAggregator,
        public router: Router,
        public sd: SessionData
    ) {
        this._http = new HttpClient();
        this._url = new UrlService();

        // Throttle the calls to applyFilters
        this._filterChangedEvent.debounceTime(300).subscribe(e => this.applyFilters());
    }

    readonly COLOR_COLLECTIONS_LABEL = 'COLOR COLLECTIONS';
    readonly COLOR_FAMILIES_LABEL = 'COLOR FAMILIES';
    readonly LRV_LABEL = 'LRV';
    readonly SELECTED_COLORS_LABEL = 'SELECTED COLORS';

    readonly DEFAULT_MAX_SELECTION_COLUMNS = 8;

    /**
     * The max number of color strips columns to show in the selection box.
     */
    maxSelectionColumns = this.DEFAULT_MAX_SELECTION_COLUMNS;

    attached() {
        this.message = "LOADING...";

        this._subscriptions = [
            this._eventBus.subscribe('region-changed', regionType => this.resetFiltersAndSelections()),
            this._eventBus.subscribe('color-search-selected', color => this.toggleSearchedColorSelection(color)),
            this._eventBus.subscribe('loading-done', e => this.onLoadingDone())
        ];

        this.loadColorCollectionsForSelectedProduct();
    }

    detached() {
        this.clearColorStrips();
        this._subscriptions.forEach(s => s.dispose());
    }

    onLoadingDone() {
        if (this.colorCollections.length == 0) {
            // If no color collections were found for this product, then clear any colors
            this.resetFiltersAndSelections();
            this.clearColorStrips();
            this.message = "No color options for product.  Click 'SKIP COLOR SELECTION' to proceed.";
        } else {
            this.message = "";
        }
    }

    message: string;

    colorCollections: Dto.ColorCollection[] = [];
    @observable selectedColorCollection: Dto.ColorCollection;

    getAllColors() {
        var colors: Dto.ColorType[] = [].concat.apply([], this.colorStrips.map(cs => cs.items));
        return colors;
    }

    toggleSearchedColorSelection(color: Dto.ColorType) {
        // Toggle the color-search color-chip instance
        this.toggleColorSelection(color);

        // Try to toggle the color-picker color-chip instance
        var colors = this.getAllColors();
        var match = colors.find(c => c.colorUID == color.colorUID);
        if (match) {
            this.toggleColorSelection(match);
        }
    }

    toggleColorSelection(color: Dto.ColorType) {

        if (!color.isSelected) {

            color.isSelected = true;

            // Updated selected colors list (if it doesn't already exist)
            this.sd.wizard.addSelectedColor(color);

        } else {
            this.deselectedColor(color);
        }
    }

    deselectedColor(color: Dto.ColorType, evt?: Event) {
        if (evt) evt.stopImmediatePropagation(); // Prevent ddl from closing when deselecting

        color.isSelected = false;

        // Remove from selected colors list
        var idx = this.sd.wizard.selectedColors.findIndex(c => c.colorUID == color.colorUID);
        if (idx > -1) {
            this.sd.wizard.selectedColors.splice(idx, 1);
        }
    }

    mouseOverColor(color: Dto.ColorType, e: MouseEvent) {
        var isSameElement = (e.target as any).classList.contains('hovered');
        if (!isSameElement) {
            (color as any).isDetailsVisible = true;
        }
    }

    mouseOutColor(color: Dto.ColorType, e: MouseEvent) {
        var rT = (e.relatedTarget as any);

        if (rT == null) {
            // Mouse went off the browser
            (color as any).isDetailsVisible = false;
            return;
        }

        // Also check for child elements
        var isSameElement = rT.classList.contains('hovered') || rT.classList.contains('color-details') || rT.classList.contains('detail') || rT.classList.contains('white-box');
        if (!isSameElement) {
            (color as any).isDetailsVisible = false;
        }
    }

    colorFamilies: Dto.ColorFamily[] = [];

    colorFamiliesSummary: string = this.COLOR_FAMILIES_LABEL;

    isColorFamiliesOpen = false;
    onColorFamiliesToggled() {
        this.isColorFamiliesOpen = !this.isColorFamiliesOpen;
        if (!this.isColorFamiliesOpen) {
            this.updateColorFamilies();
        }
    }

    updateColorFamilies() {
        var summary = this.getColorFamiliesSummary();

        // Determine if color families changed
        this._isColorFamiliesFilterChanged = summary != this.colorFamiliesSummary;

        this.colorFamiliesSummary = summary;

        if (this._isColorFamiliesFilterChanged) {
            this._filterChangedEvent.next();
        }
    }

    getColorFamiliesSummary() {
        var summary = this.colorFamilies
            .filter(s => s.isChecked)
            .map(s => s.name)
            .reduce((prev, next) => prev + (prev == '' ? '' : ', ') + next, '');

        return summary != '' ? summary : this.COLOR_FAMILIES_LABEL;
    }

    @observable lrvMin: number;
    @observable lrvMax: number;

    lrvMinChanged(oldVal: string, newVal: string) {
        this._isLrvFilterChanged = true;
        this._filterChangedEvent.next();
    }

    lrvMaxChanged(oldVal: string, newVal: string) {
        this._isLrvFilterChanged = true;
        this._filterChangedEvent.next();
    }

    colorStrips: Dto.ColorStrip[] = [];

    zoomedStrips: Dto.ColorStrip[] = [];

    selectedColorCollectionChanged(newSelection: Dto.ColorCollection, oldSelection: Dto.ColorCollection) {
        if (newSelection) {
            this._isColorCollectionFilterChanged = true;
            this.loadColorFamilies();
            this.resetSelectionBox();
            this.resetLrv();

            this._filterChangedEvent.next();
        } else {
            this._eventBus.publish('loading-done');
        }
    }

    loadColorCollectionsForSelectedProduct() {
        //if (this._lastSelectedProductNumber == (this.sd.wizard.selectedProduct && this.sd.wizard.selectedProduct.productNumber || null)) {
        //    // Prevent loading the same colors again
        //    this._eventBus.publish('loading-done');
        //    return;
        //}

        var url = this._url.build(`~/api/ColorCollections/${this.sd.region.type}/${this.sd.wizard.selectedProduct.productNumber}`);
        return this._http.get(url).then(resp => {
            //this.colorCollections = resp.content; // This approach sometimes prevented setting the selected item (selectedColorCollection) from refreshing when reloading page
            this.colorCollections.splice(0, this.colorCollections.length);
            resp.content.forEach(cc => this.colorCollections.push(cc));

            // Set the default color collection
            if (this.colorCollections.length > 0) {
                var colorPreview = this.colorCollections.find(x => x.id == 5); // 5 == color preview
                if (colorPreview) {
                    this.selectedColorCollection = colorPreview;
                } else {
                    this.selectedColorCollection = this.colorCollections[0];
                }
            } else {
                this._eventBus.publish('loading-done');
            }

            // Keep track to prevent unnecessary reloading of color collections on subsequent page visits
            this._lastSelectedProductNumber = this.sd.wizard.selectedProduct.productNumber;
        });
    }

    loadColorStrips() {
        var isExterior = this.sd.wizard.selectedProductUse.application == "Exterior";
        var isVinyl = this.sd.wizard.selectedSubstrate.longName == "Vinyl Siding";

        const qs: string[] = [];
        if (isExterior) qs.push(`exterior=true`);
        if (isVinyl) qs.push(`vinyl=true`);

        const qString = qs.length ? `?${qs.join("&")}` : "";

        var url = this._url.build(`~/api/Colors/${this.sd.wizard.selectedProduct.productNumber}/${this.selectedColorCollection.id.toString()}${qString}`);
        return this._http.get(url).then(resp => {

            // populate color navigator with all color strips
            this.colorStrips = resp.content;

            // Reset to default size in case it was changed for the last color collection
            this.maxSelectionColumns = this.DEFAULT_MAX_SELECTION_COLUMNS;

            // populate color selector with first 8 color strips
            this.zoomedStrips = this.colorStrips.filter((cs, i) => i < this.maxSelectionColumns);

            // Set selection box size
            this.maxSelectionColumns = this.constrainMinMax(this.colorStrips.length, 1, this.maxSelectionColumns);
            this.selectionBoxWidthPx = `${this.maxSelectionColumns * 10}px`;
            this.selectionBoxHeightPx = `${(this.selectedColorCollection.colorStripSize * 10)}px`;

            this._eventBus.publish('loading-done');
        });
    }

    loadColorFamilies() {
        // Load color families for the selected color collection
        var url = this._url.build('~/api/ColorFamilies/GetColorFamiliesInCollection/' + this.selectedColorCollection.id);
        this._http.get(url).then(resp => {
            this.colorFamilies = resp.content;

            // Update ddl summary
            this.updateColorFamilies();
        });
    }

    constrainMinMax(value: number, min: number, max: number) {
        if (value > max) return max;
        else if (value < min) return min;
        else return value;
    }

    selectedColorStripIndex: number = 0;
    selectionBoxLeftPx: string;
    selectionBoxHeightPx: string;
    selectionBoxWidthPx: string;

    getVisibleColorStripIndex(cs: Dto.ColorStrip) {
        var visibleColorStrips = this.colorStrips.filter(cs => !cs.isHidden);
        for (var i = 0; i < visibleColorStrips.length; i++) {
            if (cs == visibleColorStrips[i])
                return i;
        }
    }

    selectColorStripInNavigator(colorStripIndex: number) {
        var visibleColorStrips = this.colorStrips.filter(cs => !cs.isHidden);

        // The selection box should be offset to the left by half of its width (maxSelectionColumns)
        // so that the selected color strip is approximately centered within the selection window.
        var offsetSelectedIndex = colorStripIndex - Math.ceil(this.maxSelectionColumns / 2) + 1;

        // Ensure offset index is in bounds
        var maxIndex = Math.max(
            Math.min(offsetSelectedIndex, (visibleColorStrips.length - this.maxSelectionColumns)),
            0);

        // Show 8 color strips starting at the selected color strip
        this.zoomedStrips = visibleColorStrips.filter((cs, idx) => idx >= maxIndex && idx < maxIndex + this.maxSelectionColumns);

        // Set offset (current color strip width is 10px)
        this.selectionBoxLeftPx = `${maxIndex * 10}px`;

        // Ensure index is in bounds
        this.selectedColorStripIndex =
            Math.max(
                Math.min(colorStripIndex, (visibleColorStrips.length - this.maxSelectionColumns) + Math.ceil(this.maxSelectionColumns / 2) - 1),
                Math.ceil(this.maxSelectionColumns / 2));
    }

    navigateLeft() {
        var index = this.selectedColorStripIndex - 1;
        this.selectColorStripInNavigator(index);
    }

    navigateRight() {
        var index = this.selectedColorStripIndex + 1;
        this.selectColorStripInNavigator(index);
    }

    resetSelectedColors() {
        this.sd.wizard.selectedColors.forEach(c => c.isSelected = false);
        this.sd.wizard.selectedColors = [];
        this.getAllColors().filter(c => c.isSelected).forEach(c => c.isSelected = false);
    }

    resetSelectionBox() {
        this.selectColorStripInNavigator(0);
    }

    resetColorFamilies() {
        this.colorFamilies.forEach(f => f.isChecked = false);
        this.updateColorFamilies();
    }

    resetLrv() {
        this._eventBus.publish('reset-lrv');
    }

    resetFiltersAndSelections() {
        this.resetSelectionBox();
        this.resetColorFamilies();
        this.resetLrv();
        this.resetSelectedColors();
    }

    clearColorStrips() {
        this.colorStrips = [];
    }

    applyFilters() {

        console.log('Applying Color Filters');

        if (this._isColorCollectionFilterChanged) {

            this.loadColorStrips().then(() => {

                // Iterate all color chips
                this.colorStrips.forEach(cs => cs.items.forEach(c => {

                    // Ensure isSelected matches selected colors in session-data
                    c.isSelected = this.sd.wizard.selectedColors.some(sc => sc.colorUID == c.colorUID);

                }));
            });

            // Reset filter change state
            this._isColorCollectionFilterChanged = false;
        }

        if (this._isColorFamiliesFilterChanged || this._isLrvFilterChanged) {

            var checkedColorFamiliesById = Dictionary.fromArray(this.colorFamilies.filter(f => f.isChecked), f => f.id);

            var isInLrvRange = (color: Dto.ColorType) => {
                return color.lrv >= this.lrvMin && color.lrv <= this.lrvMax;
            };

            var isHiddenByColorFamilyFilter = (color: Dto.ColorType) => {
                return !(checkedColorFamiliesById.contains(color.colorFamilyId) || checkedColorFamiliesById.keys.length == 0 || color.colorFamilyId < 0);
            };

            var anyColorStripHiddenStateChanged = false;

            // Iterate all color chips
            this.colorStrips.forEach(cs => {

                var stripHasAnyVisibleChips = false;

                cs.items.forEach(c => {

                    // Hide color chip if unless one of the following three conditions is true
                    c.isHidden = isHiddenByColorFamilyFilter(c) || !isInLrvRange(c);

                    if (!c.isHidden)
                        stripHasAnyVisibleChips = true;
                });

                // Hide the color strip if it has no visible color chips
                var shouldBeHidden = !stripHasAnyVisibleChips;
                if (cs.isHidden != shouldBeHidden) {
                    cs.isHidden = shouldBeHidden;
                    anyColorStripHiddenStateChanged = true;
                }
            });

            if (anyColorStripHiddenStateChanged)
                // Reset selected index in navigator
                this.selectColorStripInNavigator(0);

            // Reset filter change states
            this._isColorFamiliesFilterChanged = false;
            this._isLrvFilterChanged = false;
        }
    }

    back() {
        this.sd.wizard.prev();
    }

    skipColorSelection() {
        this.sd.wizard.resetSelectedColors();
        this.sd.wizard.addSelectedColor(Dto.ColorType.noColor());
        this.sd.wizard.next();
        this.sd.save();
    }

    continueToReview() {
        this.sd.wizard.next();
        this.sd.save();
    }
}
